/**
 * Subscription Cancel Screen
 * Phase 2 - Priority 3: Polar.sh Checkout Integration - Phase C
 *
 * Deep link handler for canceled subscription checkout.
 * URL: dramadetector://subscription-cancel
 *
 * Flow:
 * 1. User cancels checkout on Polar.sh
 * 2. Polar redirects to deep link (or user closes browser)
 * 3. This screen shows cancellation message
 * 4. Redirects back to subscription screen
 */
import { useEffect, useState } from "react";
import { View, Text, Pressable } from "react-native";
import { useRouter } from "expo-router";
import { useTranslation } from "@dramadetector/shared";
import { trackEvent } from "@/lib/analytics/events";

export default function SubscriptionCancelScreen() {
  const router = useRouter();
  const { t } = useTranslation('subscription');
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    // Track canceled checkout
    trackEvent('subscription_checkout_canceled', {
      screen: 'subscription_cancel',
    });

    // Countdown timer
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          // Redirect to subscription screen
          router.replace("/(tabs)/subscription");
          return 0;
        }
        return prev - 1;
      });
    }, 1000) as unknown as number;

    return () => clearInterval(timer);
  }, [router]);

  const handleReturnNow = () => {
    router.replace("/(tabs)/subscription");
  };

  return (
    <View className="flex-1 bg-gray-50 items-center justify-center p-6">
      <View className="bg-white rounded-lg p-8 max-w-md w-full items-center">
        {/* Cancel Icon */}
        <View className="w-20 h-20 bg-gray-100 rounded-full items-center justify-center mb-4">
          <Text className="text-5xl">✕</Text>
        </View>

        {/* Title */}
        <Text className="text-2xl font-bold text-gray-900 mb-2 text-center">
          {t('subscription_cancel.title')}
        </Text>

        {/* Message */}
        <Text className="text-base text-gray-600 mb-6 text-center">
          {t('subscription_cancel.message')}
        </Text>

        {/* Return button */}
        <Pressable
          onPress={handleReturnNow}
          className="bg-blue-600 active:bg-blue-700 py-3 px-6 rounded-lg mb-4 w-full"
          style={{ cursor: 'pointer' }}
          accessibilityRole="button"
          accessibilityLabel={t('subscription_cancel.accessibility.return')}
        >
          <Text className="text-white text-center font-semibold">
            {t('subscription_cancel.cta')}
          </Text>
        </Pressable>

        {/* Countdown */}
        <Text className="text-sm text-gray-400 text-center">
          {t('subscription_cancel.redirecting', {
            countdown,
            plural: countdown !== 1 ? 's' : ''
          })}
        </Text>
      </View>
    </View>
  );
}
